/** Form oluştur / düzenle / doldur — panel tema stilleri */

import { PANEL } from "./panelTokens";
import { panelFormSectionTitleSx } from "./panelFormSx";

export const formBuilderTogglePanelSx = (active = true) => ({
  p: 2,
  borderRadius: PANEL.radius.cardMd,
  bgcolor: active ? PANEL.blue.soft12 : PANEL.danger.soft,
  border: `1px solid ${active ? PANEL.blue.soft25 : PANEL.danger.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  flexWrap: "wrap",
});

export const formBuilderToggleTitleSx = {
  fontWeight: 700,
  color: PANEL.text.primary,
  fontSize: "0.9375rem",
  mb: 0.25,
};

export const formBuilderToggleCaptionSx = {
  color: PANEL.text.muted,
  fontSize: "0.8125rem",
  display: "block",
};

export const formBuilderRepeatLabelSx = {
  ...panelFormSectionTitleSx,
  fontSize: "0.75rem",
  mb: 0.25,
};

export const formBuilderRepeatHintSx = {
  color: PANEL.blue.text,
  fontSize: "0.7rem",
  maxWidth: 220,
  lineHeight: 1.35,
  display: "block",
  mb: 0.5,
};

export const formBuilderRepeatStepBtnSx = (tone = "danger") => ({
  width: 32,
  height: 32,
  borderRadius: PANEL.radius.chip,
  color: PANEL.surface,
  bgcolor: tone === "danger" ? PANEL.danger.main : PANEL.accent.main,
  border: `1px solid ${tone === "danger" ? PANEL.danger.border : PANEL.accent.softBorder}`,
  "&:hover:not(:disabled)": {
    bgcolor: tone === "danger" ? "#b91c1c" : PANEL.accent.hover,
  },
  "&:disabled": {
    bgcolor: PANEL.fill.hoverAlt,
    color: PANEL.text.label,
    borderColor: PANEL.border,
  },
});

export const formBuilderRepeatInputSx = {
  width: 80,
  "& .MuiOutlinedInput-root": {
    borderRadius: PANEL.radius.input,
    bgcolor: PANEL.surface,
    fontWeight: 700,
    "& fieldset": { borderColor: PANEL.border },
    "&:hover fieldset": { borderColor: PANEL.accent.softBorder },
    "&.Mui-focused fieldset": {
      borderColor: PANEL.accent.main,
      boxShadow: `0 0 0 3px ${PANEL.accent.focusRing}`,
    },
  },
  "& .MuiInputBase-input": {
    textAlign: "center",
    color: PANEL.text.primary,
    fontWeight: 700,
  },
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

export const formBuilderToolbarStackSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 2,
  flexWrap: "wrap",
  justifyContent: { xs: "flex-start", md: "flex-end" },
};

export const formBuilderQuestionActionsSx = {
  display: "flex",
  gap: 1,
  flexWrap: "wrap",
};

const FORM_BUILDER_ADD_QUESTION_TONES = {
  text: {
    color: PANEL.blue.text,
    borderColor: PANEL.blue.soft25,
    bgcolor: PANEL.blue.soft12,
    hoverBg: "var(--panel-blue-soft-18, rgba(59, 130, 246, 0.14))",
    iconColor: PANEL.blue.main,
  },
  multiple_choice: {
    color: PANEL.accent.text,
    borderColor: PANEL.accent.softBorder,
    bgcolor: PANEL.accent.soft,
    hoverBg: PANEL.accent.soft18,
    iconColor: PANEL.accent.main,
  },
  table: {
    color: PANEL.indigo.text,
    borderColor: PANEL.indigo.softBorder,
    bgcolor: PANEL.indigo.soft,
    hoverBg: "var(--panel-indigo-soft-bg)",
    iconColor: PANEL.indigo.main,
  },
  image: {
    color: PANEL.warning.text,
    borderColor: PANEL.warning.border,
    bgcolor: PANEL.warning.soft,
    hoverBg: PANEL.warning.soft,
    iconColor: PANEL.warning.main,
  },
  rating_scale: {
    color: PANEL.danger.text,
    borderColor: PANEL.danger.border,
    bgcolor: PANEL.danger.soft,
    hoverBg: PANEL.danger.soft,
    iconColor: PANEL.danger.main,
  },
};

/** Form oluştur/düzenle — soru tipi ekleme butonları (soft renkli outlined) */
export const formBuilderAddQuestionButtonSx = (tone = "text") => {
  const palette = FORM_BUILDER_ADD_QUESTION_TONES[tone] || FORM_BUILDER_ADD_QUESTION_TONES.text;
  return {
    textTransform: "none",
    fontWeight: 600,
    color: palette.color,
    borderColor: palette.borderColor,
    bgcolor: palette.bgcolor,
    borderRadius: PANEL.radius.input,
    px: 2,
    py: 0.875,
    boxShadow: "none",
    "& .MuiButton-startIcon .MuiSvgIcon-root": {
      color: palette.iconColor,
    },
    "&:hover": {
      borderColor: palette.iconColor,
      bgcolor: palette.hoverBg,
      color: palette.color,
      boxShadow: "none",
    },
  };
};

export const formBuilderRepeatScheduleCardSx = (hasError = false) => ({
  mt: 2,
  p: { xs: 2, md: 2.5 },
  borderRadius: PANEL.radius.cardMd,
  bgcolor: PANEL.fill.muted,
  border: `1px solid ${hasError ? PANEL.danger.border : PANEL.border}`,
});

export const formBuilderRepeatScheduleHeaderIconSx = {
  width: 40,
  height: 40,
  borderRadius: PANEL.radius.avatar,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  bgcolor: PANEL.blue.soft12,
  color: PANEL.blue.text,
  border: `1px solid ${PANEL.blue.soft25}`,
  "& .MuiSvgIcon-root": { fontSize: 22 },
};

export const formBuilderRepeatScheduleRowSx = {
  p: 1.75,
  borderRadius: PANEL.radius.cardSm,
  bgcolor: PANEL.surface,
  border: `1px solid ${PANEL.border}`,
};

export const formBuilderRepeatScheduleChipSx = {
  bgcolor: PANEL.blue.soft12,
  color: PANEL.blue.text,
  fontWeight: 600,
  border: `1px solid ${PANEL.blue.soft25}`,
};

export const formBuilderRepeatScheduleValidSx = {
  mt: 2,
  p: 1.5,
  borderRadius: PANEL.radius.cardSm,
  bgcolor: PANEL.accent.soft,
  border: `1px solid ${PANEL.accent.softBorder}`,
};

export const formBuilderEditorSectionSx = {
  p: 2,
  mb: 2,
  borderRadius: PANEL.radius.cardMd,
  bgcolor: PANEL.surface,
  border: `1px solid ${PANEL.border}`,
  boxShadow: "none",
};

export const formBuilderEditorMutedSectionSx = {
  ...formBuilderEditorSectionSx,
  bgcolor: PANEL.pageBg,
};

export const formBuilderPresetToggleSx = {
  flexWrap: "wrap",
  gap: 1,
  mb: 2,
  "& .MuiToggleButtonGroup-grouped": {
    border: `1px solid ${PANEL.border} !important`,
    borderRadius: `${PANEL.radius.chip} !important`,
    mx: 0.25,
  },
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 600,
    color: PANEL.text.body,
    bgcolor: PANEL.surface,
    px: 2,
    py: 0.75,
    "&.Mui-selected": {
      color: PANEL.accent.text,
      bgcolor: `${PANEL.accent.soft} !important`,
      borderColor: `${PANEL.accent.softBorder} !important`,
    },
    "&:hover": {
      bgcolor: PANEL.fill.subtle,
    },
  },
};

export const formBuilderChoiceCardSx = (selected = false) => ({
  m: 0,
  py: 1.25,
  px: 2,
  pr: 2.5,
  borderRadius: PANEL.radius.cardSm,
  border: `1px solid ${selected ? PANEL.accent.softBorder : PANEL.border}`,
  bgcolor: selected ? PANEL.accent.soft : PANEL.surface,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    borderColor: selected ? PANEL.accent.main : PANEL.fill.hover,
    bgcolor: selected ? PANEL.accent.soft18 : PANEL.fill.subtle,
  },
  "& .MuiFormControlLabel-label": {
    color: PANEL.text.primary,
    fontWeight: selected ? 700 : 500,
    fontSize: "0.95rem",
  },
  "& .MuiRadio-root": {
    color: PANEL.text.label,
    "&.Mui-checked": { color: PANEL.accent.main },
  },
});

export const formBuilderSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: PANEL.accent.main,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: PANEL.accent.main,
  },
  "& .Mui-disabled": {
    opacity: 0.5,
  },
};
